import {AfterContentInit, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, signal,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {DcpResourceData} from '@app/model/dcp-resource-data';
import {DcpSupportData} from '@app/model/dcp-support.data';
import {DcpNetNewTermsConditions} from '@app/model/dcp-terms-and-conditions';
import {EddlDownloadAttemptEvent} from '@app/services/analytics/EDDL/events/eddl-download-attempt-event';
import {EddlDownloadSuccessEvent} from '@app/services/analytics/EDDL/events/eddl-download-success-event';
import {TermsConditionsService} from '@app/services/terms-conditions.service';
import {CanonicalId, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventService, TranslatePipe} from '@spartacus/core';
import {CurrentProductService} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {first} from 'rxjs/operators';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {NavigationLink} from '../navigation-bar/navigation-link';
import {ProductDetailsNavigationLinkService} from '../navigation-bar/product-details-navigation-link.service';
import {ProductDetailsSectionComponent} from '../product-details-section/product-details-section.component';
import {TermsAndConditionsComponent} from '../../../checkout/checkout-terms-conditions/terms-and-conditions.component';
import {ProductResourceTileComponent} from './product-resource-tile/product-resource-tile.component';
import {ProductSingleResourceTileComponent} from './product-single-resource-tile/product-single-resource-tile.component';
import {ProductPageDecoratorService} from '@app/product/services/product-page-decorator.service';

@UntilDestroy()
@Component({
  selector: 'dcp-product-resources',
  templateUrl: './product-resources.component.html',
  styleUrls: ['./product-resources.component.scss'],
  imports: [
    TermsAndConditionsComponent,
    ProductResourceTileComponent,
    ProductSingleResourceTileComponent,
    TranslatePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProductResourcesComponent
  extends ProductDetailsSectionComponent
  implements OnInit, OnDestroy, AfterContentInit
{
  entryLimit = 4;
  resources = signal<DcpResourceData[]>([]);
  support = signal<DcpSupportData | undefined>(undefined);
  terms = signal<string | undefined>(undefined);
  privacy = signal<string | undefined>(undefined);
  termsConditionsProductEntries = signal<any[]>([]);
  resourcesExtended = signal(false);
  hideTermsAndConditionsSection = signal(false);

  limitedResources = computed(() =>
    this.resourcesExtended() ? this.resources() : this.resources().slice(0, this.entryLimit),
  );

  navLink: NavigationLink = {
    headline: 'productDetails.resources',
    id: 'resources-section',
  };

  constructor(
    dcpProductUtilitiesService: DcpProductUtilitiesService,
    protected currentProductService: CurrentProductService,
    protected changeDetection: ChangeDetectorRef,
    protected productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    protected productPageDecoratorService: ProductPageDecoratorService,
    private termsConditionsService: TermsConditionsService,
    private jsonService: JsonService,
    private eventService: EventService,
  ) {
    super(currentProductService, changeDetection, productDetailsNavigationLinkService, dcpProductUtilitiesService, productPageDecoratorService);
  }

  isVisible(): boolean {
    return (
      !!this.product &&
      (this.resources().length > 0 ||
        !!this.terms() ||
        !!this.privacy() ||
        !!this.support()?.knowledgebase ||
        !!this.support()?.email ||
        !this.hideTermsAndConditionsSection())
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
  }

  toggleResourcesWithKeyboard(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.toggleResources();
    }
  }

  toggleResources(): void {
    this.resourcesExtended.update((isExtended) => !isExtended);
  }

  prepareData(): void {
    if (!!this.product?.data) {
      this.resources.set((this.findAttribute('resources') as DcpResourceData[]) || []);
      this.privacy.set(this.findAttribute('privacy') as string);
      this.support.set(this.findAttribute('support') as DcpSupportData);
      this.terms.set(this.findAttribute('terms') as string);
      this.termsConditionsProductEntries.set(
        this.termsConditionsService.createTermsConditionsEntriesFromProduct(this.product, this.entitlements),
      );
      if (!this.dcpProductUtilitiesService.isSapProduct(this.product)) {
        this.setPartnerProductTerms();
      }
    }
  }

  setPartnerProductTerms(): void {
    this.fetchTermsAndCondition(this.dcpProductUtilitiesService.getCanonicalId(this.product))
      .pipe(untilDestroyed(this), first())
      .subscribe((netNewTermsConditions: DcpNetNewTermsConditions[] | undefined) => {
        if (netNewTermsConditions?.[0]?.urlTermsConds?.length) {
          this.terms.set(netNewTermsConditions?.[0]?.urlTermsConds);
          this.changeDetection.detectChanges();
        }
        if (!this.terms()?.length) {
          this.fetchTermsAndCondition(
            this.dcpProductUtilitiesService.getCanonicalId(this.termsConditionsProductEntries()[0].product),
          )
            .pipe(untilDestroyed(this), first())
            .subscribe((netNewTermsConditions: DcpNetNewTermsConditions[] | undefined) => {
              this.terms.set(netNewTermsConditions?.[0]?.urlTermsConds || this.terms());
              this.changeDetection.detectChanges();
            });
        }
      });
  }

  private fetchTermsAndCondition(canonicalId: CanonicalId): Observable<DcpNetNewTermsConditions[] | undefined> {
    return this.termsConditionsService.fetchTermsConditionsByCanonicalId(canonicalId, {salesOrg: undefined});
  }

  findAttribute(entryType: string): any {
    const rawAttr = this.product?.data?.find((entry) => entry.type === entryType);
    return rawAttr ? this.jsonService.parseSafely(rawAttr.value) : undefined;
  }

  onTermsClick(clickEvent: MouseEvent): void {
    const href = (clickEvent.target as HTMLAnchorElement).href;
    if (!href?.includes('.pdf')) return;
    const attempt = new EddlDownloadAttemptEvent();
    attempt.url = href;
    const success = new EddlDownloadSuccessEvent();
    success.url = href;
    setTimeout(() => {
      this.eventService.dispatch(attempt);
      this.eventService.dispatch(success);
    });
  }

  onEntriesEmpty(e: boolean) {
    this.hideTermsAndConditionsSection.set(e);
  }
}
